import { observable, action, computed } from 'mobx'
import { addHours } from 'date-fns'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type {
  Consultation,
  ConsultationStatus,
} from '@/features/jobs/consultations/Consultation'
import { ScheduledDayViewModel } from './ScheduledDayViewModel'
import {
  generateSchedulableDays,
  type SchedulableDay,
} from './schedule-generation'
import { task } from 'mobx-task'
import type { ConsultationStore } from '@/features/jobs/consultations/ConsultationStore'
import type { SnackbarFactory } from '@/features/shell/snackbars'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { tapAction, toStream } from '@/utils/mobx-util'
import type { TimeRange } from '@/infra/time-range'

export default class ConsultationSchedulerViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly daysShown = 7
  readonly startingHour = 8
  readonly endingHour = 21
  readonly startingMinute = 0

  readonly setAvailabilityDialog = new ModalViewModel()
  readonly confirmAvailabilityDialog = new ModalViewModel()
  readonly reviewAvailabilityDialog = new ModalViewModel()
  readonly availabilitySubmittedDialog = new ModalViewModel()

  @observable accessor scheduledDays: ScheduledDayViewModel[] = []
  @observable accessor dayIndex = 0
  @observable accessor selectedDayIndex = 0

  constructor(
    private readonly _consultationStore: ConsultationStore,
    private readonly _newSnackbar: SnackbarFactory,
    public readonly job: SubmittedJob,
    private readonly _isSettingAvailabilityAllowed: () => boolean,
  ) {
    this._lifecycle.manage(
      toStream(() => this.consultation, true).pipe(
        tapAction(this.populateScheduledDays),
      ),
    )
  }

  /**
   * The interval in minutes between time slots.
   */
  @computed
  get intervalInMinutes(): number {
    return this.consultation?.requestedLength ?? 60
  }

  /**
   * The currently selected day.
   */
  @computed
  get selectedDay() {
    return this.scheduledDays[this.dayIndex]
  }

  /**
   * The currently selected time ranges.
   */
  @computed
  get selectedTimeRanges() {
    return this.scheduledDays.flatMap((sd) => sd.selectedTimeSlotsConsolidated)
  }

  /**
   * Whether the selected day has any time slots.
   */
  @computed
  get hasSelectedTimes(): boolean {
    return this.selectedTimeRanges.length > 0
  }

  /**
   * The consultation.
   */
  @computed
  get consultation(): Consultation | null {
    return this.job.match({
      tax: (type) => type.consultation,
      consultation: (type) => type.consultation,
      default: () => null,
    })
  }

  /**
   * The consultation status.
   */
  @computed
  get consultationStatus(): ConsultationStatus {
    return this.consultation?.status ?? 'Unknown'
  }

  /**
   * Whether the consultation has availability set.
   */
  @computed
  get isAvailabilitySet(): boolean {
    return !this.consultation
      ? false
      : this.consultation.availability.length > 0
  }

  /**
   * Whether the availability can be set.
   */
  @computed
  get isSettingAvailabilityAllowed(): boolean {
    return this._isSettingAvailabilityAllowed()
  }

  /**
   * The currently saved availability from the consultation.
   */
  @computed
  get currentlySavedAvailability() {
    if (!this.consultation?.availability.length) {
      return []
    }

    return this.consultation.availability
  }

  /** @inheritdoc */
  @action.bound
  activate(): void {
    this._lifecycle.activate()
  }

  /** @inheritdoc */
  deactivate(): void {
    this._lifecycle.deactivate()
  }

  /**
   * Submits the availability.
   */
  submitAvailability = task.resolved(async () => {
    if (!this.consultation) {
      return
    }

    if (!this.hasSelectedTimes) {
      return
    }

    const isRescheduling = this.isAvailabilitySet
    await this._consultationStore.setConsultationAvailability(
      this.job.id,
      this.selectedTimeRanges,
    )

    this._closeDialogs()
    if (isRescheduling) {
      // Don't show the completed dialog if rescheduling.
      return
    }

    this.showCompletedDialog()
  })

  /**
   * Shows the set availability dialog.
   */
  @action.bound
  showSetAvailabilityDialog(): void {
    this.setDay(0)
    this.setAvailabilityDialog.show()
  }

  /**
   * Shows the review availability dialog.
   */
  @action.bound
  showReviewAvailabilityDialog(): void {
    this.reviewAvailabilityDialog.show()
  }

  /**
   * Dialog confirming the availability has been submitted.
   */
  @action.bound
  showCompletedDialog(): void {
    this.availabilitySubmittedDialog.show()
  }

  /**
   * Sets up the next 7 days and populates the user's previously selected availability.
   */
  @action.bound
  populateScheduledDays(): void {
    // Get user's previous availability
    const prevAvailability = this.consultation?.availability?.flatMap((a) =>
      a.decomposeRangeInIntervals(this.intervalInMinutes),
    )

    // Determine the earliest time to show.
    // If a slot was already booked, then the earliest time will be
    // at least 12 hours from now.
    // Otherwise, the earliest time will be 12 hours from now.
    const now = new Date()
    const earliestTime = addHours(now, 12)

    // Generate schedulable days
    const schedulableDays = generateSchedulableDays({
      now,
      daysToShow: this.daysShown,
      intervalInMinutes: this.intervalInMinutes,
      startingHour: this.startingHour,
      endingHour: this.endingHour,
      earliestTime,
    })

    this.scheduledDays = this._mapToScheduledDayViewModels(
      schedulableDays,
      prevAvailability,
    )

    // Update time slots for the current day
    this.dayIndex = 0
  }

  /**
   * Creates view models for the scheduled days.
   */
  private _mapToScheduledDayViewModels(
    schedulableDays: SchedulableDay[],
    previouslySelectedTimeSlots?: TimeRange[],
  ): ScheduledDayViewModel[] {
    return schedulableDays.map((day) => {
      const vm = new ScheduledDayViewModel(day.date, day.timeSlots)

      // If there are previously selected time slots, mark them as selected
      if (previouslySelectedTimeSlots?.length) {
        const matchingTimeSlots = day.timeSlots.filter((slot) =>
          previouslySelectedTimeSlots.some((prev) => prev.isEqual(slot)),
        )

        if (matchingTimeSlots.length > 0) {
          vm.selectTimeSlots(matchingTimeSlots)
        }
      }

      return vm
    })
  }

  /**
   * Closes all dialogs.s
   */
  @action.bound
  private _closeDialogs() {
    this.availabilitySubmittedDialog.close()
    this.setAvailabilityDialog.close()
    this.confirmAvailabilityDialog.close()
    this.reviewAvailabilityDialog.close()
  }

  /**
   * Sets the selected date.
   *
   * @param index The index of the day to select
   */
  @action.bound
  setDay(index: number): void {
    this.dayIndex = index
  }

  /**
   * Cancels the availability dialog.
   */
  @action.bound
  close() {
    this.setAvailabilityDialog.close()
    this.confirmAvailabilityDialog.close()
    this.reviewAvailabilityDialog.close()
  }

  /**
   * Confirms the selected availability by showing the confirmation dialog.
   */
  @action.bound
  confirmAvailability() {
    if (!this.hasSelectedTimes) {
      this._newSnackbar((s) =>
        s
          .saying('No times selected', 'Please select at least one time slot')
          .type('error')
          .withKey(NO_TIMES_SELECTED_SNACKBAR_KEY)
          .until(this.setAvailabilityDialog.close$)
          .autoDismiss()
          .show(),
      )
      return
    }
    this.confirmAvailabilityDialog.show()
  }

  /**
   * Reloads the scheduled days list.
   */
  @action.bound
  reset(): void {
    this.dayIndex = 0
    // Set up the next days and populate time slots
    this.populateScheduledDays()
  }
}

/**
 * Key for snackbar deduplication.
 */
const NO_TIMES_SELECTED_SNACKBAR_KEY = Symbol('no-times-selected')
